import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ResumenEmpresa from './ResumenEmpresa';
import CalendarioLicencias from './CalendarioLicencias';
import ListaEmpleados from './ListaEmpleados';
import HistorialLicencias from './HistorialLicencias';
import ImportarPeriodos from './ImportarPeriodos';
import { useDashboardData } from '../hooks/useDashboardData';
import { useAuth } from '../hooks/useAuth';
import './PanelControl.css';

type Pestana = 'resumen' | 'calendario' | 'empleados' | 'historial' | 'importar';

const PanelControl: React.FC = () => {
  const { usuario, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [pestanaActiva, setPestanaActiva] = useState<Pestana>('resumen');
  const { loading, error, data, retry } = useDashboardData();

  const manejarLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const renderHeader = () => (
    <header className="dashboard-header">
      <h1>Consulta de Licencias</h1>
      <div className="user-info">
        <div className="user-details">
          <i className="fas fa-user-circle"></i>
          <div>
            <span className="user-name">{usuario?.nombreCompleto}</span>
            <span className="user-role">{usuario?.nombreEmpresa} - {usuario?.rol}</span>
          </div>
        </div>
        <button className="btn-logout" onClick={manejarLogout} title="Cerrar sesion">
          <i className="fas fa-sign-out-alt"></i>
          Salir
        </button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="dashboard">
        {renderHeader()}
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        {renderHeader()}
        <div className="dashboard-error">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Error al cargar los datos</h2>
          <p>{error}</p>
          <button className="btn-retry" onClick={retry}>
            <i className="fas fa-redo"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { empleados, periodos, balances, resumenDepartamentos, departamentos, tiposLicencia } = data;

  const renderizarContenidoPestana = () => {
    switch (pestanaActiva) {
      case 'resumen':
        return <ResumenEmpresa
          empleados={empleados}
          balances={balances}
          periodos={periodos}
          resumenesDepartamentos={resumenDepartamentos}
        />;
      case 'calendario':
        return <CalendarioLicencias periodos={periodos} empleados={empleados} />;
      case 'empleados':
        return <ListaEmpleados
          empleados={empleados}
          balances={balances}
          periodos={periodos}
          departamentos={departamentos}
          tiposLicencia={tiposLicencia}
          onDatosActualizados={retry}
          esAdmin={isAdmin()}
        />;
      case 'historial':
        return <HistorialLicencias
          periodos={periodos}
          empleados={empleados}
          tiposLicencia={tiposLicencia}
          balances={balances}
          onDatosActualizados={retry}
          esAdmin={isAdmin()}
        />;
      case 'importar':
        return <ImportarPeriodos onDatosActualizados={retry} />;
      default:
        return null;
    }
  };

  return (
    <div className="dashboard">
      {renderHeader()}

      <nav className="dashboard-nav">
        <button
          className={`nav-button ${pestanaActiva === 'resumen' ? 'active' : ''}`}
          onClick={() => setPestanaActiva('resumen')}
        >
          <i className="fas fa-chart-pie"></i>
          Resumen General
        </button>
        <button
          className={`nav-button ${pestanaActiva === 'calendario' ? 'active' : ''}`}
          onClick={() => setPestanaActiva('calendario')}
        >
          <i className="fas fa-calendar"></i>
          Calendario
        </button>
        <button
          className={`nav-button ${pestanaActiva === 'empleados' ? 'active' : ''}`}
          onClick={() => setPestanaActiva('empleados')}
        >
          <i className="fas fa-users"></i>
          Empleados
        </button>
        <button
          className={`nav-button ${pestanaActiva === 'historial' ? 'active' : ''}`}
          onClick={() => setPestanaActiva('historial')}
        >
          <i className="fas fa-list"></i>
          Historial
        </button>
        {isAdmin() && (
          <button
            className={`nav-button ${pestanaActiva === 'importar' ? 'active' : ''}`}
            onClick={() => setPestanaActiva('importar')}
          >
            <i className="fas fa-file-import"></i>
            Importar
          </button>
        )}
      </nav>

      <main className="dashboard-content">
        {renderizarContenidoPestana()}
      </main>
    </div>
  );
};

export default PanelControl;
